import React, { forwardRef, useImperativeHandle, useState } from 'react';

import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

interface StepOneProps {
  setContractDetails: (data: {
    levels?: number;
    yearToRetain?: number;
    creatorRole?: string;
    contractName?: string;
  }) => void;
  contractDetails: {
    levels: number;
    yearToRetain: number;
    creatorRole: string;
    contractName: string;
  };
}

export interface StepOneRef {
  validateStep: () => boolean;
}

const StepOne = forwardRef<StepOneRef, StepOneProps>(
  ({ setContractDetails, contractDetails }, ref) => {
    const [levels, setLevels] = useState(contractDetails.levels || 0);
    const [yearToRetain, setYearToRetain] = useState(
      contractDetails.yearToRetain || 0
    );
    const [creatorRole, setCreatorRole] = useState(
      contractDetails.creatorRole || ''
    );
    const [contractName, setContractName] = useState(
      contractDetails.contractName || ''
    );

    // Display states for empty inputs
    const [levelsDisplay, setLevelsDisplay] = useState('');

    // Validation states
    const [errors, setErrors] = useState({
      levels: '',
      yearToRetain: '',
      creatorRole: '',
      contractName: '',
    });

    // Available retention years
    const retentionYears = [1, 3, 5, 7];

    const validateLevels = (value: number): string => {
      if (value <= 0) return 'Number of levels must be greater than 0';
      if (value > 10) return 'Number of levels cannot exceed 10';
      return '';
    };

    const validateYearToRetain = (value: number): string => {
      if (value <= 0) return 'Retention period must be selected';
      if (!retentionYears.includes(value))
        return 'Please select a valid retention period';
      return '';
    };

    const validateCreatorRole = (value: string): string => {
      if (!value.trim()) return 'Your role is required';
      if (value.trim().length < 2) return 'Role must be at least 2 characters';
      if (value.trim().length > 50) return 'Role cannot exceed 50 characters';
      return '';
    };

    const validateContractName = (value: string): string => {
      if (!value.trim()) return 'Contract name is required';
      if (value.trim().length < 2)
        return 'Contract name must be at least 2 characters';
      if (value.trim().length > 100)
        return 'Contract name cannot exceed 100 characters';
      return '';
    };

    const validateStep = (): boolean => {
      const levelsError = validateLevels(levels);
      const yearToRetainError = validateYearToRetain(yearToRetain);
      const creatorRoleError = validateCreatorRole(creatorRole);
      const contractNameError = validateContractName(contractName);

      setErrors({
        levels: levelsError,
        yearToRetain: yearToRetainError,
        creatorRole: creatorRoleError,
        contractName: contractNameError,
      });

      return (
        !levelsError &&
        !yearToRetainError &&
        !creatorRoleError &&
        !contractNameError
      );
    };

    useImperativeHandle(ref, () => ({
      validateStep,
    }));

    const handleLevelsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      // Only allow digits
      if (value && !/^\d+$/.test(value)) {
        return;
      }

      const numValue = value ? parseInt(value) : 0;
      setLevels(numValue);
      setLevelsDisplay(value);

      const error = validateLevels(numValue);
      setErrors((prev) => ({ ...prev, levels: error }));
      setContractDetails({ levels: numValue });
    };

    const handleYearToRetainChange = (event: any) => {
      const value = event.target.value;
      setYearToRetain(value);

      const error = validateYearToRetain(value);
      setErrors((prev) => ({ ...prev, yearToRetain: error }));
      setContractDetails({ yearToRetain: value });
    };

    const handleCreatorRoleChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const value = event.target.value;
      setCreatorRole(value);
      const error = validateCreatorRole(value);
      setErrors((prev) => ({ ...prev, creatorRole: error }));
      setContractDetails({ creatorRole: value });
    };

    const handleContractNameChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const value = event.target.value;
      setContractName(value);
      const error = validateContractName(value);
      setErrors((prev) => ({ ...prev, contractName: error }));
      setContractDetails({ contractName: value });
    };

    // Helper function to format year display
    const formatYearDisplay = (year: number): string => {
      return year === 1 ? '1 year' : `${year} years`;
    };

    return (
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h6"
          sx={{ mb: 3, color: '#1976d2', fontWeight: 600 }}
        >
          Contract Setup Details
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 250px' },
              gap: 3,
              alignItems: 'center',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                color: '#333',
              }}
            >
              Contract Name
            </Typography>
            <TextField
              label="Contract Name"
              value={contractName}
              type="text"
              error={!!errors.contractName}
              helperText={errors.contractName}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
              inputProps={{ maxLength: 100 }}
              onChange={handleContractNameChange}
            />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 250px' },
              gap: 3,
              alignItems: 'center',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                color: '#333',
              }}
            >
              How many levels of signature are expected?
            </Typography>
            <TextField
              label="Number of Levels"
              value={levelsDisplay}
              type="number"
              error={!!errors.levels}
              helperText={errors.levels}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
              inputProps={{
                min: 1,
                max: 10,
                step: 1,
              }}
              onChange={handleLevelsChange}
            />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 250px' },
              gap: 3,
              alignItems: 'center',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                color: '#333',
              }}
            >
              How long would you like to retain the document?
            </Typography>
            <FormControl
              error={!!errors.yearToRetain}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            >
              <InputLabel>Retention Period</InputLabel>
              <Select
                value={yearToRetain}
                label="Retention Period"
                onChange={handleYearToRetainChange}
              >
                {retentionYears.map((year) => (
                  <MenuItem key={year} value={year}>
                    {formatYearDisplay(year)}
                  </MenuItem>
                ))}
              </Select>
              {errors.yearToRetain && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 1.5 }}
                >
                  {errors.yearToRetain}
                </Typography>
              )}
            </FormControl>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 250px' },
              gap: 3,
              alignItems: 'center',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                color: '#333',
              }}
            >
              Your role
            </Typography>
            <TextField
              label="Your Role"
              value={creatorRole}
              type="text"
              error={!!errors.creatorRole}
              helperText={errors.creatorRole}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
              inputProps={{ maxLength: 50 }}
              onChange={handleCreatorRoleChange}
            />
          </Box>
        </Box>

        {(errors.levels ||
          errors.yearToRetain ||
          errors.creatorRole ||
          errors.contractName) && (
          <Alert severity="warning" sx={{ mt: 3, borderRadius: 2 }}>
            Please fix the validation errors above before proceeding.
          </Alert>
        )}
      </Box>
    );
  }
);

StepOne.displayName = 'StepOne';

export default StepOne;
